import React, { useState } from 'react';
import { MapPin, Calendar, Building2, Zap, X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { trackRecord } from '../mock/mockData';

const TrackRecord = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#081221] to-[#0d63ce]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">
            Esperienza Internazionale
          </h1>
          <p className="text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Oltre 614 MWp installati in 5 paesi con progetti di eccellenza
          </p>
        </div>
      </section>

      {/* Global Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-6xl font-bold font-serif text-[#0d63ce] mb-4">
                {trackRecord.totalMWp}+
              </div>
              <div className="text-gray-600 font-medium text-lg">MWp Installati</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold font-serif text-[#0d63ce] mb-4">
                {trackRecord.countries.length}
              </div>
              <div className="text-gray-600 font-medium text-lg">Paesi</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold font-serif text-[#0d63ce] mb-4">
                {trackRecord.countries.reduce((acc, c) => acc + c.projects, 0)}+
              </div>
              <div className="text-gray-600 font-medium text-lg">Progetti Completati</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold font-serif text-[#0d63ce] mb-4">20+</div>
              <div className="text-gray-600 font-medium text-lg">Anni di Esperienza</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Presenza Internazionale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Seleziona un paese per visualizzare i dettagli
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {trackRecord?.countries?.map((country, index) => (
              <button
                key={index}
                onClick={() => {
                  setSelectedCountry(country);
                  if (country.gallery && country.gallery.length > 0) {
                    setShowGallery(true);
                    setCurrentImageIndex(0);
                  }
                }}
                className={`p-6 rounded-xl transition-all transform hover:-translate-y-1 ${
                  selectedCountry?.name === country.name
                    ? 'bg-gradient-to-br from-[#0d63ce] to-[#18a06b] text-white shadow-2xl scale-105'
                    : 'bg-white text-gray-800 shadow-lg hover:shadow-xl'
                }`}
              >
                <MapPin
                  className={`mx-auto mb-4 ${
                    selectedCountry?.name === country.name ? 'text-white' : 'text-[#0d63ce]'
                  }`}
                  size={32}
                />
                <div className="text-3xl font-bold font-serif mb-2">{country.mwp} MWp</div>
                <div className="font-semibold">{country.name}</div>
              </button>
            ))}
          </div>

          {selectedCountry && (
            <div className="mt-12 bg-white rounded-2xl p-8 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <Zap className="mx-auto text-[#0d63ce] mb-4" size={48} />
                  <div className="text-4xl font-bold text-[#0d63ce] mb-2">
                    {selectedCountry.mwp} MWp
                  </div>
                  <div className="text-gray-600">Capacità Installata</div>
                </div>
                <div className="text-center">
                  <Building2 className="mx-auto text-[#18a06b] mb-4" size={48} />
                  <div className="text-4xl font-bold text-[#18a06b] mb-2">
                    {selectedCountry.projects}
                  </div>
                  <div className="text-gray-600">Progetti Completati</div>
                </div>
                <div className="text-center">
                  <MapPin className="mx-auto text-[#081221] mb-4" size={48} />
                  <div className="text-4xl font-bold text-[#081221] mb-2">
                    {selectedCountry.name}
                  </div>
                  <div className="text-gray-600">Mercato</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* World Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Presenza Globale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Impianti realizzati in 5 paesi tra Europa, Africa e Mediterraneo
            </p>
          </div>

          {/* World Map - Clean Version */}
          <div className="relative bg-gradient-to-br from-[#f5f7fb] to-white rounded-3xl shadow-2xl p-8 overflow-hidden">
            <div className="relative">
              <img 
                src="/world_map_clean.png" 
                alt="Mappa mondiale presenza TerniEnergia Progetti" 
                className="w-full h-auto rounded-xl"
              />
            </div>

            {/* Custom Legend */}
            <div className="mt-8 flex justify-center gap-12">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded bg-gradient-to-br from-[#0d63ce] to-[#18a06b]" />
                <span className="text-base font-medium text-gray-700">Paesi con presenza TEP</span>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="mt-12 grid grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-[#0d63ce] mb-2">5</div>
                <div className="text-gray-600 font-medium">Paesi</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#18a06b] mb-2">614</div>
                <div className="text-gray-600 font-medium">MWp Totali</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#0d63ce] mb-2">50+</div>
                <div className="text-gray-600 font-medium">Impianti</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {showGallery && selectedCountry && selectedCountry.gallery && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X size={32} />
          </button>

          <div className="max-w-7xl w-full">
            {/* Gallery Header */}
            <div className="text-center mb-8">
              <h2 className="font-serif text-4xl font-bold text-white mb-2">
                {selectedCountry.name}
              </h2>
              <p className="text-white/80 text-lg">
                {currentImageIndex + 1} / {selectedCountry.gallery.length} progetti
              </p>
            </div>

            {/* Main Image */}
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={selectedCountry.gallery[currentImageIndex].image}
                alt={selectedCountry.gallery[currentImageIndex].name}
                className="w-full h-[600px] object-cover"
              />
              
              {/* Image Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-8">
                <h3 className="font-serif text-3xl font-bold text-white mb-2">
                  {selectedCountry.gallery[currentImageIndex].name}
                </h3>
                <div className="flex items-center gap-6 text-white/90">
                  <span className="flex items-center gap-2">
                    <Zap size={20} />
                    {selectedCountry.gallery[currentImageIndex].mwp} MWp
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin size={20} />
                    {selectedCountry.gallery[currentImageIndex].location}
                  </span>
                  {selectedCountry.gallery[currentImageIndex].client && (
                    <span className="flex items-center gap-2">
                      <Building2 size={20} />
                      {selectedCountry.gallery[currentImageIndex].client}
                    </span>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              {selectedCountry.gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => 
                      prev === 0 ? selectedCountry.gallery.length - 1 : prev - 1
                    )}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-4 rounded-full shadow-xl transition-all"
                  >
                    <ChevronLeft size={32} className="text-[#081221]" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => 
                      prev === selectedCountry.gallery.length - 1 ? 0 : prev + 1
                    )}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-4 rounded-full shadow-xl transition-all"
                  >
                    <ChevronRight size={32} className="text-[#081221]" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {selectedCountry.gallery.length > 1 && (
              <div className="flex gap-4 mt-6 overflow-x-auto pb-4">
                {selectedCountry.gallery.map((project, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-32 h-20 rounded-lg overflow-hidden transition-all ${
                      idx === currentImageIndex 
                        ? 'ring-4 ring-white shadow-2xl scale-110' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clienti Section */}
      <section className="py-24 bg-gradient-to-br from-[#f8fafc] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-gray-900 mb-6">
              I Nostri Clienti
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Partner di eccellenza nei nostri progetti internazionali
            </p>
          </div>

          {/* Logos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
            {[
              { num: 115, ext: 'png', name: 'Circet' },
              { num: 116, ext: 'png', name: 'Acea' },
              { num: 117, ext: 'png', name: 'Enfinity' },
              { num: 118, ext: 'png', name: 'X-Elio' },
              { num: 119, ext: 'png', name: 'Verbund' },
              { num: 130, ext: 'png', name: 'ENI' },
              { num: 133, ext: 'png', name: 'Altenia' },
              { num: 132, ext: 'png', name: 'Ortus' },
              { num: 131, ext: 'png', name: 'Fiamma 2000' },
              { num: 128, ext: 'png', name: 'EDF' },
              { num: 127, ext: 'png', name: 'Aeroporti di Roma' },
              { num: 120, ext: 'jpeg', name: 'Terna' },
              { num: 121, ext: 'jpeg', name: 'Buzzi Unicem' },
              { num: 122, ext: 'png', name: 'Ferrero' },
              { num: 123, ext: 'png', name: 'Alchimia' },
              { num: 124, ext: 'png', name: 'CAF' },
              { num: 125, ext: 'png', name: 'Circet' },
              { num: 126, ext: 'png', name: 'Bester' }
            ].map((logo) => (
              <div
                key={logo.num}
                className="flex items-center justify-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all hover:scale-105"
              >
                <img
                  src={`/logos/image${logo.num}.${logo.ext}`}
                  alt={logo.name}
                  className="max-h-16 w-auto object-contain transition-all"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default TrackRecord;
